import { LGraphNode, LiteGraph } from "litegraph.js";

/**
 * CameraNode
 * ----------
 * Takes snapshots of the canvas input. Each shot is collected into a zip
 * that also includes a settings.txt with the current page URL so the
 * exact graph state can be reloaded later.
 *
 * Custom-drawn shutter button + thumbnail strip of captured shots.
 */
export class CameraNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Camera";

    this.properties = {};

    // State
    this._shots = [];        // { blob, name, img }
    this._status = "Ready";
    this._lastFrame = null;
    this._captureCanvas = document.createElement("canvas");
    this._captureCtx = this._captureCanvas.getContext("2d");

    // Animation state
    this._flashAlpha = 0;    // shutter flash feedback
    this._shutterScale = 1;  // shutter press animation

    // Input
    this.addInput("Canvas", "canvas");

    // No default widgets — we draw our own buttons
    this.size = [220, 260];
    this.mode = LiteGraph.ALWAYS;
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";
  }

  // ─── Button hit areas (relative to node) ──────────────────────────
  _getShutterRect() {
    const w = this.size[0];
    return { cx: w / 2, cy: 46, r: 18 };
  }

  _getActionButtons() {
    const w = this.size[0];
    const y = 74;
    const btnW = (w - 30) / 2;
    return {
      download: { x: 10, y, w: btnW, h: 20, label: "Download Zip" },
      clear:    { x: 15 + btnW, y, w: btnW, h: 20, label: "Clear" },
    };
  }

  // ─── Snapshot logic ───────────────────────────────────────────────
  _takeSnapshot() {
    const input = this._lastFrame;
    if (!input) {
      this._status = "No input!";
      return;
    }

    // Trigger flash + press animation
    this._flashAlpha = 0.6;
    this._shutterScale = 0.85;

    this._captureCanvas.width = input.width;
    this._captureCanvas.height = input.height;
    this._captureCtx.clearRect(0, 0, input.width, input.height);
    this._captureCtx.drawImage(input, 0, 0);

    this._captureCanvas.toBlob((blob) => {
      if (!blob) return;
      const idx = this._shots.length + 1;
      const img = new Image();
      const url = URL.createObjectURL(blob);
      img.onload = () => URL.revokeObjectURL(url);
      img.src = url;
      this._shots.push({
        blob,
        name: `shot_${String(idx).padStart(3, "0")}.png`,
        img,
      });
      this._status = `${this._shots.length} shot${this._shots.length !== 1 ? "s" : ""}`;
    }, "image/png");
  }

  _getSettingsUrl() {
    return window.location.href;
  }

  async _downloadZip() {
    if (this._shots.length === 0) {
      this._status = "No shots to download!";
      return;
    }

    this._status = "Zipping...";

    const { default: JSZip } = await import("jszip");
    const zip = new JSZip();

    for (const shot of this._shots) {
      zip.file(shot.name, shot.blob);
    }

    const settingsContent = [
      "Futures Suite — Settings",
      "========================",
      "",
      "Reload this exact configuration by opening:",
      this._getSettingsUrl(),
      "",
      `Snapshots: ${this._shots.length}`,
      `Date: ${new Date().toISOString()}`,
    ].join("\n");

    zip.file("settings.txt", settingsContent);

    const blob = await zip.generateAsync({ type: "blob" }, (meta) => {
      this._status = `Zipping ${Math.round(meta.percent)}%`;
    });

    this._download(blob, `snapshots_${Date.now()}.zip`);
    this._status = `Saved ${this._shots.length} shots`;
  }

  _download(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    setTimeout(() => {
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 5000);
    }, 100);
  }

  // ─── Click handling ───────────────────────────────────────────────
  onMouseDown(e, localPos) {
    const x = localPos[0];
    const y = localPos[1];

    // Shutter button
    const s = this._getShutterRect();
    const dx = x - s.cx, dy = y - s.cy;
    if (dx * dx + dy * dy <= s.r * s.r) {
      this._takeSnapshot();
      return true;
    }

    // Action buttons
    const btns = this._getActionButtons();
    for (const [key, b] of Object.entries(btns)) {
      if (x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h) {
        if (key === "download") this._downloadZip();
        else if (key === "clear") {
          this._shots = [];
          this._status = "Cleared";
        }
        return true;
      }
    }

    return false;
  }

  // ─── Drawing ──────────────────────────────────────────────────────
  onDrawForeground(ctx) {
    const w = this.size[0];
    const h = this.size[1];

    // Animate flash decay
    if (this._flashAlpha > 0) this._flashAlpha *= 0.85;
    if (this._flashAlpha < 0.01) this._flashAlpha = 0;

    // Animate shutter spring back
    this._shutterScale += (1 - this._shutterScale) * 0.2;

    // ── Shutter button ──
    const s = this._getShutterRect();
    ctx.save();
    ctx.translate(s.cx, s.cy);
    ctx.scale(this._shutterScale, this._shutterScale);

    // Outer ring
    ctx.beginPath();
    ctx.arc(0, 0, s.r, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(255,255,255,0.5)";
    ctx.lineWidth = 2.5;
    ctx.stroke();

    // Inner filled circle
    ctx.beginPath();
    ctx.arc(0, 0, s.r - 5, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.fill();

    ctx.restore();

    // Flash overlay
    if (this._flashAlpha > 0) {
      ctx.fillStyle = `rgba(255,255,255,${this._flashAlpha})`;
      ctx.fillRect(0, 0, w, h);
    }

    // ── Action buttons ──
    const btns = this._getActionButtons();
    for (const [key, b] of Object.entries(btns)) {
      const isDownload = key === "download";
      const hasShots = this._shots.length > 0;

      ctx.fillStyle = isDownload
        ? (hasShots ? "rgba(74,158,255,0.3)" : "rgba(255,255,255,0.06)")
        : "rgba(255,255,255,0.08)";
      this._roundRect(ctx, b.x, b.y, b.w, b.h, 4);
      ctx.fill();

      ctx.strokeStyle = isDownload
        ? (hasShots ? "rgba(74,158,255,0.5)" : "rgba(255,255,255,0.1)")
        : "rgba(255,255,255,0.15)";
      ctx.lineWidth = 1;
      this._roundRect(ctx, b.x, b.y, b.w, b.h, 4);
      ctx.stroke();

      ctx.fillStyle = isDownload
        ? (hasShots ? "#8ac4ff" : "rgba(255,255,255,0.25)")
        : "rgba(255,255,255,0.5)";
      ctx.font = "9px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(b.label, b.x + b.w / 2, b.y + 13);
      ctx.textAlign = "left";
    }

    // ── Thumbnail strip ──
    const stripY = 100;
    const thumbSize = 50;
    const gap = 4;
    const stripH = h - stripY - 22;

    ctx.fillStyle = "rgba(0,0,0,0.25)";
    this._roundRect(ctx, 10, stripY, w - 20, stripH, 4);
    ctx.fill();

    if (this._shots.length === 0) {
      ctx.fillStyle = "rgba(255,255,255,0.15)";
      ctx.font = "10px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("No snapshots yet", w / 2, stripY + stripH / 2 + 3);
      ctx.textAlign = "left";
    } else {
      const cols = Math.max(1, Math.floor((w - 24) / (thumbSize + gap)));
      const rows = Math.max(1, Math.floor(stripH / (thumbSize + gap)));
      const visible = cols * rows;
      const startIdx = Math.max(0, this._shots.length - visible);

      ctx.save();
      ctx.beginPath();
      ctx.rect(10, stripY, w - 20, stripH);
      ctx.clip();

      for (let i = 0; i < visible && (startIdx + i) < this._shots.length; i++) {
        const shot = this._shots[startIdx + i];
        if (!shot.img || !shot.img.naturalWidth) continue;

        const col = i % cols;
        const row = Math.floor(i / cols);
        const tx = 14 + col * (thumbSize + gap);
        const ty = stripY + 4 + row * (thumbSize + gap);

        const aspect = shot.img.naturalWidth / shot.img.naturalHeight;
        let tw = thumbSize, th = thumbSize;
        if (aspect > 1) th = thumbSize / aspect;
        else tw = thumbSize * aspect;

        // Thumbnail background
        ctx.fillStyle = "rgba(0,0,0,0.4)";
        this._roundRect(ctx, tx, ty, thumbSize, thumbSize, 3);
        ctx.fill();

        ctx.drawImage(shot.img, tx + (thumbSize - tw) / 2, ty + (thumbSize - th) / 2, tw, th);

        // Index pill
        ctx.fillStyle = "rgba(0,0,0,0.5)";
        this._roundRect(ctx, tx + 1, ty + 1, 14, 10, 2);
        ctx.fill();
        ctx.fillStyle = "rgba(255,255,255,0.6)";
        ctx.font = "7px sans-serif";
        ctx.fillText(String(startIdx + i + 1), tx + 3, ty + 9);
      }

      ctx.restore();

      if (this._shots.length > visible) {
        ctx.fillStyle = "rgba(255,255,255,0.25)";
        ctx.font = "8px sans-serif";
        ctx.fillText(`+${this._shots.length - visible} more`, 14, stripY + stripH - 4);
      }
    }

    // ── Shot count badge ──
    if (this._shots.length > 0) {
      const badgeX = w - 22;
      const badgeY = stripY - 3;
      ctx.fillStyle = "#4a9eff";
      ctx.beginPath();
      ctx.arc(badgeX, badgeY, 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.font = "bold 10px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(String(this._shots.length), badgeX, badgeY + 4);
      ctx.textAlign = "left";
    }

    // ── Status ──
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.font = "9px sans-serif";
    ctx.fillText(this._status, 10, h - 8);
  }

  _roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  onExecute() {
    const input = this.getInputData(0);
    if (input) {
      this._lastFrame = input;
    }
  }
}

LiteGraph.registerNodeType("export/Camera", CameraNode);
